import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../admin/api';
import { useLang } from '../i18n/LanguageContext';

/**
 * Read-only availability calendar shown in the Free Consultation section.
 * Default: every date is "open" (peach dot).
 * Overrides come from /api/availability and color the date as
 *   - "limited" (amber)
 *   - "booked"  (muted gray + strikethrough)
 * Admin manages overrides from the admin panel.
 *
 * Props:
 *   onSelectDate?: (isoDate, status) => void  // only fires on open/limited dates
 */
const STATUS_STYLES = {
  open: {
    cell: 'bg-[#D4AF37]/15 hover:bg-[#D4AF37]/30 text-white border-[#D4AF37]/30',
    dot: 'bg-[#D4AF37]',
  },
  limited: {
    cell: 'bg-amber-400/15 hover:bg-amber-400/30 text-amber-100 border-amber-400/45',
    dot: 'bg-amber-300',
  },
  booked: {
    cell: 'bg-white/5 text-white/30 line-through border-white/10 cursor-not-allowed',
    dot: 'bg-white/25',
  },
};

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

const buildMonthGrid = (anchor) => {
  const first = startOfMonth(anchor);
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const startWeekday = (first.getDay() + 6) % 7; // Monday-first
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= lastDay; day++) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const AvailabilityCalendar = ({ onSelectDate }) => {
  const { lang, t } = useLang();
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()));
  const [overrides, setOverrides] = useState({});

  // Fetch overrides for the visible 2-month window when anchor changes.
  useEffect(() => {
    const start = startOfMonth(anchor);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0);
    api
      .get('/api/availability', {
        params: { from_date: isoDate(start), to_date: isoDate(end) },
      })
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((row) => {
          map[row.date] = { status: row.status, note: row.note || '' };
        });
        setOverrides(map);
      })
      .catch(() => setOverrides({}));
  }, [anchor]);

  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : lang === 'fr' ? 'fr' : 'en', {
      month: 'long', year: 'numeric',
    }),
    [lang]
  );
  const weekdayFmt = useMemo(
    () => new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : lang === 'fr' ? 'fr' : 'en', { weekday: 'short' }),
    [lang]
  );
  const weekdays = useMemo(() => {
    // Monday first
    const out = [];
    const base = new Date(2024, 0, 1); // Monday
    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      out.push(weekdayFmt.format(d).replace('.', ''));
    }
    return out;
  }, [weekdayFmt]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const renderMonth = (offset) => {
    const m = addMonths(anchor, offset);
    const cells = buildMonthGrid(m);
    return (
      <div className="flex-1 min-w-0" data-testid={`avail-month-${offset}`}>
        <div className="text-center mb-3 sm:mb-4">
          <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37]/80">
            {t('avail.bookable', 'Live availability')}
          </div>
          <div className="font-serif-display text-xl sm:text-2xl text-white capitalize mt-1">
            {monthFmt.format(m)}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">
          {weekdays.map((wd) => (
            <div key={wd} className="text-center py-1">{wd}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {cells.map((d, idx) => {
            if (!d) return <div key={`e-${idx}`} className="aspect-square" />;
            const iso = isoDate(d);
            const past = d < today;
            const ov = overrides[iso];
            const status = past ? 'booked' : ov?.status || 'open';
            const styles = STATUS_STYLES[status] || STATUS_STYLES.open;
            const disabled = status === 'booked';
            const note = ov?.note || '';
            const title = past
              ? ''
              : status === 'booked'
              ? note || (lang === 'fr' ? 'Complet' : lang === 'ar' ? 'محجوز بالكامل' : 'Fully booked')
              : status === 'limited'
              ? note || (lang === 'fr' ? 'Quelques places' : lang === 'ar' ? 'أماكن محدودة' : 'Limited slots')
              : (lang === 'fr' ? 'Disponible' : lang === 'ar' ? 'متاح' : 'Available');
            return (
              <button
                key={iso}
                type="button"
                disabled={disabled}
                title={title}
                aria-label={`${iso} — ${title}`}
                onClick={() => !disabled && onSelectDate && onSelectDate(iso, status)}
                data-testid={`avail-cell-${iso}`}
                data-status={status}
                className={`relative aspect-square rounded-lg border text-[12px] sm:text-sm font-medium transition-colors flex flex-col items-center justify-center ${styles.cell} ${past ? 'opacity-40' : ''}`}
              >
                <span>{d.getDate()}</span>
                <span className={`mt-1 w-1.5 h-1.5 rounded-full ${styles.dot}`} />
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full" data-testid="availability-calendar">
      {/* Month navigation */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={() => setAnchor((a) => addMonths(a, -1))}
          aria-label="Previous month"
          data-testid="avail-prev"
          className="w-9 h-9 rounded-full bg-white/10 border border-[#D4AF37]/30 text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors flex items-center justify-center"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 h-px bg-[#D4AF37]/20" />
        <button
          type="button"
          onClick={() => setAnchor((a) => addMonths(a, 1))}
          aria-label="Next month"
          data-testid="avail-next"
          className="w-9 h-9 rounded-full bg-white/10 border border-[#D4AF37]/30 text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors flex items-center justify-center"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Two months side-by-side on desktop, stacked on mobile */}
      <div className="flex flex-col md:flex-row gap-6 md:gap-8">
        {renderMonth(0)}
        <div className="hidden md:block">{renderMonth(1)}</div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] tracking-[0.18em] uppercase text-white/65">
        <span className="inline-flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" /> {t('avail.open', 'Available')}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" /> {t('avail.limited', 'Limited slots')}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-white/25" /> {t('avail.booked', 'Fully booked')}
        </span>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
